/** Spending Lux from the dashboard (pure; tested in luxSpend.test.ts): when a click must be confirmed, what the
 * confirmation and the toasts say, and the toast stack. The server prices everything; amounts are integer
 * hundredths of a Lux (100 = 1.00 Lux) and cents.
 *
 * Owner rules: every click that spends Lux asks first (cost and the balance after), unless the user switched that
 * off ("Don't ask me again", stored on the user: `PATCH /v1/me {confirm_lux_spend}`; Lux & billing › Settings).
 * A click within the plan's included uses spends nothing and never asks. Every spend and refund the user caused
 * shows a toast with a link to its line in the usage log; at most three toasts at once. Visitors' searches on the
 * website never toast. Not enough Lux always shows the popover (Buy Lux / Upgrade), whatever the preference. */
import { fmtLux, fmtMoney, type LuxWords } from "./lux.ts";

/** Where to switch the confirmation back on (the Settings card of Lux & billing). */
export const SETTINGS_PATH = "/lux#lux-settings";
export const SETTINGS_LABEL = "Lux & billing › Settings";

/** Every dashboard click that spends Lux, with what asks before it (the audit of the owner's rule). */
export const SPEND_POINTS: readonly { where: string; action: string; confirm: string }[] = [
  { where: "Designer › new design: Design with AI · 1.00 Lux", action: "design", confirm: "Lux confirm popover" },
  { where: "Sketch › Design with AI card", action: "design", confirm: "Lux confirm popover" },
  { where: "Sketch › Assist bar: Roof hints beyond the included uses (once per design; later hints on it never ask)", action: "sketch.roof_hints", confirm: "Lux confirm popover" },
  { where: "Sketch › Assist bar: 3D surroundings beyond the included sessions (once per page session; showing them again never asks)", action: "sketch.surroundings", confirm: "Lux confirm popover" },
  { where: "Permit workspace › Review › Generate plan set (first one of a design)", action: "plan_set", confirm: "Lux confirm popover" },
  { where: "Billing › Extra users with Lux", action: "seat", confirm: "its own confirm step (exact Lux quoted, expected_lux; one Idempotency-Key per confirmation, `PurchaseIntent`)" },
];

/** A purchase the user confirms once (extra users with Lux): one Idempotency-Key from the moment its confirmation
 * opens, reused by every retry of that confirmation (a double click, a retry after a timeout or an error), so the
 * server buys it once; `next()` (a new confirmation) starts a new purchase with a new key. */
export class PurchaseIntent {
  private current: string | null = null;
  private readonly make: () => string;
  constructor(make: () => string = randomKey) {
    this.make = make;
  }
  /** A new purchase intent (the confirmation opens): a fresh key. */
  next(): string {
    this.current = this.make();
    return this.current;
  }
  /** The key of the purchase being confirmed (a request sent before any `next()` still gets one). */
  key(): string {
    return this.current ?? this.next();
  }
}

function randomKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

export interface SpendRequest {
  /** Hundredths of a Lux the click spends (0: included, nothing spent). */
  cost: number;
  /** What it buys: "Design with AI", "Roof hints". */
  label: string;
  /** The Lux the company can spend now (null: unknown). */
  balance: number | null;
  /** About what `cost` is worth, in cents (the plan's Lux price), when known. */
  worthMinor?: number | null;
  /** The server said the wallet cannot pay. */
  canPay?: boolean;
  /** What the spend lasts, said under the price ("Once per page: …"). */
  note?: string | null;
  /** Where "Buy Lux" / "Upgrade" come back to after paying (a dashboard path, e.g. the Permit workspace's Review
   * step): checkout returns there instead of Lux & billing. */
  returnTo?: string | null;
  /** Runs before the out-of-Lux popover leaves for billing (e.g. saving the page's unsaved work). */
  beforeLeave?: () => Promise<unknown> | void;
}

/** What the wallet can spend on an action: actions that may not use the Lux reserved for automatic designs
 * (`uses_reserved` false on the rate card: plan sets, searches, Assist) see the balance without it. */
export function spendableLux(
  wallet: { balance: number; monthly?: { reserved_left?: number | null } | null; reserved_lots?: number | null } | null | undefined,
  usesReserved: boolean,
): number | null {
  if (!wallet) return null;
  if (usesReserved) return wallet.balance;
  return wallet.balance - Math.max(wallet.monthly?.reserved_left ?? 0, 0) - Math.max(wallet.reserved_lots ?? 0, 0);
}

/** Whether a click asks first: it spends Lux and the user has not switched confirmations off. */
export function needsConfirm(cost: number | null | undefined, confirmPref: boolean | null | undefined): boolean {
  return (cost ?? 0) > 0 && confirmPref !== false;
}

/** The wallet cannot pay this click: the out-of-Lux popover shows whatever the preference. */
export function cannotPay(req: SpendRequest): boolean {
  if (req.cost <= 0) return false;
  // the server prices and checks everything (designs may use the 1.00 Lux overdraft): its answer wins
  if (req.canPay != null) return !req.canPay;
  return req.balance != null && req.balance < req.cost;
}

/** The confirmation's words. */
export function confirmCopy(req: SpendRequest, w?: LuxWords): { title: string; balance: string; worth: string | null; button: string } {
  const after = req.balance == null ? null : req.balance - req.cost;
  if (w) {
    const lux = (h: number) => fmtLux(h, w.locale);
    return {
      title: w.t("lux.spend.title", { cost: lux(req.cost) }),
      balance: req.balance == null ? req.label : w.t("lux.spend.balance", { label: req.label, balance: lux(req.balance), after: lux(Math.max(after ?? 0, 0)) }),
      worth: req.worthMinor ? w.t("lux.spend.worth", { price: fmtMoney(req.worthMinor, "USD", w.locale) }) : null,
      button: w.t("lux.spend.button", { cost: lux(req.cost) }),
    };
  }
  return {
    title: `Spend ${fmtLux(req.cost)} Lux?`,
    balance:
      req.balance == null ? req.label : `${req.label} · you have ${fmtLux(req.balance)} Lux, ${fmtLux(Math.max(after ?? 0, 0))} left after`,
    worth: req.worthMinor ? `≈ ${fmtMoney(req.worthMinor)}` : null,
    button: `Spend ${fmtLux(req.cost)} Lux`,
  };
}

export type LuxToastKind = "spent" | "refunded";

export interface LuxToast {
  id: number;
  kind: LuxToastKind;
  /** Hundredths of a Lux (positive). */
  amount: number;
  label: string;
  /** The balance after (null: unknown). */
  left?: number | null;
  /** Why it was refunded ("found no roof data"). */
  reason?: string | null;
  /** The usage log, filtered to this action. */
  href?: string;
}

export const MAX_TOASTS = 3;

/** The two lines of a Lux toast. */
export function toastText(t: Pick<LuxToast, "kind" | "amount" | "label" | "left" | "reason">, w?: LuxWords): { headline: string; detail: string | null } {
  if (w) {
    const what = t.kind === "refunded" && t.reason ? w.t("lux.toast.refundReason", { label: t.label, reason: t.reason }) : t.label;
    return {
      headline: w.t("lux.toast.headline", { kind: t.kind, amount: fmtLux(t.amount, w.locale), what }),
      detail: t.left == null ? null : w.t("lux.toast.left", { amount: fmtLux(t.left, w.locale) }),
    };
  }
  const verb = t.kind === "spent" ? "spent" : "refunded";
  const what = t.kind === "refunded" && t.reason ? `${t.label} ${t.reason}` : t.label;
  return { headline: `${fmtLux(t.amount)} Lux ${verb} · ${what}`, detail: t.left == null ? null : `${fmtLux(t.left)} left` };
}

/** Adds a toast; the oldest go when more than `max` are shown. Nothing is added for 0 Lux. */
export function pushToast(list: readonly LuxToast[], t: LuxToast, max = MAX_TOASTS): LuxToast[] {
  if (!(t.amount > 0)) return [...list];
  return [...list, t].slice(-max);
}

/** The usage log filtered to an action (Lux & billing › Activity). */
export function activityHref(action?: string | null): string {
  return action ? `/lux?activity=${encodeURIComponent(action)}#lux-activity` : "/lux#lux-activity";
}

/** A toast for what a click charged (`charged` hundredths, 0 when included or replayed: no toast). */
export function spendToast(id: number, action: string, label: string, charged: number, left: number | null | undefined): LuxToast | null {
  if (!(charged > 0)) return null;
  return { id, kind: "spent", amount: charged, label, left: left ?? null, href: activityHref(action) };
}

/** An action's price from the wallet's rate card (hundredths per block), else `fallback`. */
export function ratePrice(rateCard: readonly { action: string; price: number }[] | null | undefined, action: string, fallback: number): number {
  return rateCard?.find((r) => r.action === action)?.price ?? fallback;
}

// ------------------------------------------------------------------ Buy Lux and coming back

/** A dashboard path to come back to, or null: only "/…" paths of this site (no "//host", no scheme, no "\\"). */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (!raw || raw.length > 500) return null;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) return null;
  if (/[\u0000-\u001f]/.test(raw)) return null;
  return raw;
}

/** Lux & billing (its packs or plans), remembering where to come back to after paying. */
export function buyLuxHref(returnTo: string | null | undefined, section: "packs" | "plans" = "packs"): string {
  const back = safeReturnPath(returnTo);
  return back ? `/lux?return=${encodeURIComponent(back)}#${section}` : `/lux#${section}`;
}

function withParam(path: string, key: string, value: string): string {
  const [beforeHash, hash] = path.split("#", 2) as [string, string | undefined];
  const sep = beforeHash.includes("?") ? "&" : "?";
  return `${beforeHash}${sep}${key}=${encodeURIComponent(value)}${hash ? `#${hash}` : ""}`;
}

/** The Lux checkout's return URLs: straight back to `returnTo` (with `checkout=paid|canceled`), else Lux & billing. */
export function checkoutReturnUrls(origin: string, returnTo: string | null | undefined): { success_url: string; cancel_url: string } {
  const back = safeReturnPath(returnTo);
  if (!back) return { success_url: `${origin}/lux?checkout=success`, cancel_url: `${origin}/lux?checkout=cancelled` };
  return { success_url: `${origin}${withParam(back, "checkout", "paid")}`, cancel_url: `${origin}${withParam(back, "checkout", "canceled")}` };
}

/** What a checkout return says (`?checkout=`): paid, canceled, or nothing. */
export function checkoutResult(raw: string | null | undefined): "paid" | "canceled" | null {
  if (raw === "paid" || raw === "success") return "paid";
  if (raw === "canceled" || raw === "cancelled" || raw === "cancel") return "canceled";
  return null;
}

/** Before leaving for billing: runs the page's `beforeLeave` (saving its work). Leaving is allowed only when it
 * resolved; a failed save keeps the user on the page with the reason and a Retry (never leave unsaved work). */
export async function prepareLeave(beforeLeave: (() => Promise<unknown> | void) | undefined, w?: LuxWords): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    await beforeLeave?.();
    return { ok: true };
  } catch (e) {
    const fallback = w ? w.t("lux.spend.noAnswer") : "Solar OS did not answer. Check your connection and try again.";
    const message = e instanceof Error && e.message ? e.message : typeof e === "string" && e ? e : fallback;
    return { ok: false, error: w ? w.t("lux.spend.notSaved", { message }) : `Your work could not be saved, so you are still here. ${message}` };
  }
}
